import { useEffect, useRef } from 'react';
import './Modal.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared accessible dialog primitive. Gives every modal the same behavior so
 * components stop re-implementing the overlay idiom:
 *   - role="dialog" + aria-modal + aria-labelledby (from `title`)
 *   - focus trap: focus the first control on open, cycle Tab/Shift+Tab, and
 *     RESTORE focus to the previously-focused element on close
 *   - Escape closes; click on the overlay (outside the panel) closes
 *   - body scroll lock while open
 *
 * Controlled: render nothing unless `open`. `onClose` fires on Escape/overlay.
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  labelledBy,
  className = '',
  // The overlay centres its panel. A command palette wants to sit near the top
  // instead, and that is an OVERLAY property, not a panel one — hence a second
  // class hook rather than a `top` override that would fight `align-items`.
  overlayClassName = '',
  closeOnOverlay = true,
}) {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    lastFocusedRef.current = document.activeElement;
    const modal = modalRef.current;
    const focusables = modal ? modal.querySelectorAll(FOCUSABLE) : [];
    (focusables.length ? focusables[0] : modal)?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key === 'Tab' && modal) {
        const items = modal.querySelectorAll(FOCUSABLE);
        if (!items.length) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      const last = lastFocusedRef.current;
      if (last && typeof last.focus === 'function') last.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const titleId = labelledBy || (title ? 'ui-modal-title' : undefined);
  return (
    <div
      className={`ui-modal-overlay ${overlayClassName}`.trim()}
      ref={overlayRef}
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === overlayRef.current) onClose?.();
      }}
    >
      <div
        className={`ui-modal ${className}`.trim()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-label={titleId ? undefined : 'Dialog'}
        tabIndex={-1}
      >
        {title && (
          <h3 id={titleId} className="ui-modal__title">
            {title}
          </h3>
        )}
        {children}
      </div>
    </div>
  );
}
